import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useUiPrefs } from "../lib/ui-prefs.js";
import { visualRect } from "../lib/zoom.js";

const CONTEXT_MENU_MARGIN = 8;

// Позиционирует ПКМ-меню как в оригинальном Telegram: меню «растёт» из точки
// клика. По умолчанию — вправо-вниз от курсора; если справа/снизу не хватает
// места, меню раскрывается в противоположную сторону ОТ ТОЙ ЖЕ ТОЧКИ (низ
// меню совпадает с курсором), а не прижимается к краю экрана. Прижатие к краю
// остаётся аварийным вариантом — когда меню не влезает ни вниз, ни вверх.
// Используется списком диалогов и сообщениями чата — стили общие
// (.dialog-context-menu в styles.css).
//
// Масштаб интерфейса (`zoom` на <html>) разводит две системы координат —
// подробности и замеры по браузерам в lib/zoom.ts. Раньше визуальные значения
// писались в стили напрямую, и при масштабе > 1 меню уезжало вправо-вниз от
// курсора тем сильнее, чем крупнее масштаб. Считаем всё в визуальных px
// (rect — через visualRect, иначе Safari даст логические) и делим на масштаб
// ровно один раз — при записи в стиль.
interface MenuPosition {
  top: number;
  left: number;
  maxHeight?: number;
  transformOrigin: string;
}

export const ContextMenuPositioner = ({
  anchorX,
  anchorY,
  className = "dialog-context-menu",
  role = "menu",
  children
}: {
  anchorX: number;
  anchorY: number;
  // Позиционер переиспользуется и для всплывашки превью чата — там свой
  // класс панели вместо стилей меню.
  className?: string;
  role?: string;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const uiScale = useUiPrefs((state) => state.uiScale);
  const scale = uiScale > 0 ? uiScale : 1;
  const [position, setPosition] = useState<MenuPosition>({
    top: anchorY / scale,
    left: anchorX / scale,
    transformOrigin: "top left"
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Визуальные размеры меню — в одном пространстве с anchorX/Y и innerWidth.
    const rect = visualRect(el);
    const maxH = vh - 2 * CONTEXT_MENU_MARGIN;
    // Высота, из которой считаем раскрытие: если меню длиннее экрана, оно
    // всё равно будет ограничено maxHeight со скроллом.
    const height = Math.min(rect.height, maxH);

    let left = anchorX;
    let originX = "left";
    if (left + rect.width + CONTEXT_MENU_MARGIN > vw) {
      // Справа не хватает — раскрываем влево от точки клика.
      const flipped = anchorX - rect.width;
      left =
        flipped >= CONTEXT_MENU_MARGIN
          ? flipped
          : Math.max(CONTEXT_MENU_MARGIN, vw - rect.width - CONTEXT_MENU_MARGIN);
      originX = "right";
    }

    let top = anchorY;
    let originY = "top";
    if (top + height + CONTEXT_MENU_MARGIN > vh) {
      // Снизу не хватает — раскрываем ВВЕРХ от точки клика: нижний край меню
      // совпадает с курсором. Так ведёт себя ПКМ-меню в оригинальном ТГ.
      const flipped = anchorY - height;
      top =
        flipped >= CONTEXT_MENU_MARGIN
          ? flipped
          : // Не влезает ни вниз, ни вверх — прижимаем к верхнему краю.
            CONTEXT_MENU_MARGIN;
      originY = "bottom";
    }

    setPosition({
      top: top / scale,
      left: left / scale,
      transformOrigin: `${originY} ${originX}`,
      ...(rect.height > maxH ? { maxHeight: maxH / scale } : {})
    });
  }, [anchorX, anchorY, scale]);

  const style: CSSProperties = {
    top: position.top,
    left: position.left,
    // Анимация pop-in должна «вырастать» из точки клика, а не из угла экрана.
    transformOrigin: position.transformOrigin,
    ...(position.maxHeight ? { maxHeight: position.maxHeight, overflowY: "auto" } : {})
  };

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      onClick={(event) => event.stopPropagation()}
      role={role}
    >
      {children}
    </div>
  );
};
